import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useConsumerAuth } from '../../context/ConsumerAuthContext';
import axios from 'axios';
import { 
  CheckCircle, FileText, Clock, ArrowRight, 
  Download, Mail, Phone, Shield, Home
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PASSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { consumer, token, loading: authLoading } = useConsumerAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmingPayment, setConfirmingPayment] = useState(false);

  const orderId = searchParams.get('order_id');

  useEffect(() => {
    // If not authenticated after auth check completes, still show success
    if (!authLoading) {
      if (token && orderId) {
        fetchOrder();
        confirmPayment();
      } else {
        // Show success without order details
        setLoading(false);
      }
    }
  }, [orderId, token, authLoading]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API}/pas/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrder(response.data);
    } catch (err) {
      console.error('Failed to fetch order:', err);
    } finally {
      setLoading(false);
    }
  };

  const confirmPayment = async () => {
    setConfirmingPayment(true);
    try {
      await axios.post(`${API}/pas/orders/${orderId}/payment-success`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.error('Failed to confirm payment:', err);
    } finally {
      setConfirmingPayment(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#012169]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#012169] text-white">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#E31837] rounded-full flex items-center justify-center font-bold text-xl">B</div>
            <span className="text-xl font-bold">BETH Finance</span>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-12">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Order Confirmed!</h1>
          <p className="text-gray-600">Your Credit Enhancement Report is being prepared</p>
        </div>

        {/* Order Details Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
          <div className="p-6 border-b border-gray-200 bg-[#012169]">
            <div className="flex items-center justify-between">
              <div className="text-white">
                <p className="text-sm text-white/70">Order ID</p>
                <p className="text-lg font-bold" data-testid="order-id">{orderId}</p>
              </div>
              <div className="text-right text-white">
                <p className="text-sm text-white/70">Total Paid</p>
                <p className="text-2xl font-bold">${order?.total_price || 0}</p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-4">
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
              <FileText className="w-8 h-8 text-[#012169]" />
              <div>
                <p className="font-semibold text-gray-900">{order?.tier_name || 'Credit Enhancement Report'}</p>
                <p className="text-sm text-gray-500">PAS - Payment Assurance System</p>
              </div>
            </div>

            {order?.addons && order.addons.length > 0 && (
              <div className="border-t border-gray-200 pt-4">
                <p className="text-sm font-medium text-gray-500 mb-2">Add-ons Included:</p>
                <ul className="space-y-1">
                  {order.addons.map((addon, idx) => (
                    <li key={idx} className="text-sm text-gray-700 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      {addon.replace('addon_', '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
              <Clock className="w-6 h-6 text-blue-600" />
              <div>
                <p className="font-medium text-blue-900">Estimated Delivery</p>
                <p className="text-sm text-blue-700">
                  {order?.estimated_delivery 
                    ? new Date(order.estimated_delivery).toLocaleDateString('en-US', { 
                        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
                      })
                    : '3-5 business days'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* What Happens Next */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">What Happens Next?</h2>
          
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-[#012169] text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">1</div>
              <div>
                <p className="font-medium text-gray-900">Report Preparation</p>
                <p className="text-sm text-gray-600">Our team will analyze your financial profile and prepare your credit enhancement report.</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-[#012169] text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">2</div>
              <div>
                <p className="font-medium text-gray-900">Email Notification</p>
                <p className="text-sm text-gray-600">You'll receive an email when your report is ready for download.</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-[#012169] text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">3</div>
              <div>
                <p className="font-medium text-gray-900">Download & Use</p>
                <p className="text-sm text-gray-600">Download your report and present it to lenders alongside your loan applications.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Support */}
        <div className="bg-gray-100 rounded-xl p-6 mb-6">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Shield className="w-5 h-5" /> Need Help?
          </h3>
          <div className="flex flex-col sm:flex-row gap-4">
            <a 
              href="mailto:support@bethfinance.com" 
              className="flex items-center gap-2 text-[#012169] hover:underline"
            >
              <Mail className="w-4 h-4" /> support@bethfinance.com
            </a>
            <a 
              href="tel:1-800-BETH-FIN" 
              className="flex items-center gap-2 text-[#012169] hover:underline"
            >
              <Phone className="w-4 h-4" /> 1-800-BETH-FIN
            </a>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={() => navigate('/beth-finance/dashboard')}
            className="flex-1 px-6 py-3 bg-[#012169] hover:bg-[#01337d] text-white rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
            data-testid="go-to-dashboard"
          >
            <Home className="w-5 h-5" /> Go to Dashboard
          </button>
          <button
            onClick={() => navigate('/beth-finance/apply')}
            className="flex-1 px-6 py-3 border-2 border-[#012169] text-[#012169] hover:bg-[#012169] hover:text-white rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
            data-testid="apply-for-loan"
          >
            Apply for Loan <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PASSuccess;
